/**
 * Default parameters for the CFB ELO rating system
 */

export const DEFAULT_PARAMS = {
  // Base rating for all teams
  baseRating: 1500,

  // K-factor: how much ratings change per game (higher = more volatile)
  baseK: 32,

  // Margin of victory weight (0 = ignore MOV, 1 = full weight)
  marginWeight: 0.5,

  // Maximum MOV multiplier (caps blowout impact)
  maxMarginMultiplier: 1.6,

  // Home field advantage in ELO points (typically 50-70 for ~3 point advantage)
  homeAdvantage: 55,

  // Conference starting bonus multiplier (1.0 = use defaults, 0 = no bonus)
  conferenceBias: 1.0,

  // Initial rating deviation (uncertainty) - Glicko-style
  initialRD: 150,

  // Minimum rating deviation
  minRD: 50,

  // RD decay per game (how fast uncertainty decreases)
  rdDecay: 0.92,
};

// Conference baseline adjustments (added to base rating)
export const CONFERENCE_ADJUSTMENTS = {
  SEC: 75,
  "Big Ten": 60,
  "Big 12": 40,
  ACC: 40,
  "Mountain West": -20,
  "American Athletic": -30,
  "Sun Belt": -40,
  "Mid-American": -50,
  "Conference USA": -50,
  Independent: 0,
  // FCS conferences get significant penalty
  FCS: -200,
};

// Power conferences for filtering/display
export const POWER_CONFERENCES = ["SEC", "Big Ten", "Big 12", "ACC"];

export const GROUP_OF_5_CONFERENCES = [
  "Mountain West",
  "American Athletic",
  "Sun Belt",
  "Mid-American",
  "Conference USA",
];

// Conference colors for visualization
export const CONFERENCE_COLORS = {
  SEC: "#ffd700",
  "Big Ten": "#cc0000",
  "Big 12": "#003366",
  ACC: "#4169e1",
  "Mountain West": "#4b0082",
  "American Athletic": "#ff4500",
  "Sun Belt": "#f4c430",
  "Mid-American": "#8b4513",
  "Conference USA": "#008080",
  Independent: "#808080",
};

// Parameter ranges for sliders with detailed explanations
export const PARAM_RANGES = {
  baseK: {
    min: 10,
    max: 50,
    step: 1,
    label: "K-Factor",
    description: "Controls how much ratings change after each game. Higher values mean ratings are more volatile and react more strongly to individual results.",
    visual: {
      low: { label: "Low (10-20)", effect: "Stable ratings, slow to change" },
      mid: { label: "Medium (25-35)", effect: "Balanced responsiveness" },
      high: { label: "High (40-50)", effect: "Volatile, big swings per game" },
    },
    example: "At K=32, an upset win might shift ratings by ~25 points. At K=50, the same upset could move ratings by ~40 points.",
  },
  marginWeight: {
    min: 0,
    max: 1,
    step: 0.05,
    label: "Margin of Victory",
    description: "How much the point differential matters. At 0, only wins/losses count. At 1, blowouts are weighted significantly more than close games.",
    visual: {
      low: { label: "Low (0-0.3)", effect: "A win is a win" },
      mid: { label: "Medium (0.4-0.6)", effect: "Blowouts matter more" },
      high: { label: "High (0.7-1)", effect: "Domination rewarded heavily" },
    },
    example: "A 35-7 win vs a 21-20 win: at weight=0 they're equal. At weight=0.5, the blowout earns ~40% more rating points.",
  },
  maxMarginMultiplier: {
    min: 1,
    max: 2.5,
    step: 0.1,
    label: "MOV Cap",
    description: "Caps the maximum bonus from margin of victory. Prevents running up the score from inflating ratings excessively.",
    visual: {
      low: { label: "Low (1.0-1.3)", effect: "Blowouts barely rewarded" },
      mid: { label: "Medium (1.4-1.8)", effect: "Reasonable blowout bonus" },
      high: { label: "High (1.9-2.5)", effect: "Domination highly valued" },
    },
    example: "At cap=1.6, even a 50-point blowout only gets 1.6x the rating change of a 1-point win.",
  },
  homeAdvantage: {
    min: 0,
    max: 100,
    step: 5,
    label: "Home Field",
    description: "ELO points added to the home team's rating when calculating expected outcomes. ~55 points ≈ 3 point spread advantage.",
    visual: {
      low: { label: "Low (0-30)", effect: "~0-1.5 point advantage" },
      mid: { label: "Medium (40-70)", effect: "~2-4 point advantage" },
      high: { label: "High (80-100)", effect: "~5-6 point advantage" },
    },
    example: "At 55, a home team is expected to win by ~3 points against an equally-rated opponent.",
  },
  conferenceBias: {
    min: 0,
    max: 2,
    step: 0.1,
    label: "Conference Bias",
    description: "Multiplier for conference-based starting ratings. At 0, all teams start equal. At 1, SEC teams start +75, Big Ten +60, etc.",
    visual: {
      low: { label: "Low (0-0.4)", effect: "Near-equal starting ratings" },
      mid: { label: "Medium (0.8-1.2)", effect: "Standard conference tiers" },
      high: { label: "High (1.5-2)", effect: "Strong conference hierarchy" },
    },
    example: "At bias=1, SEC teams start at 1575 vs MAC teams at 1450. At bias=0, everyone starts at 1500.",
  },
};
